/**
 * erpHandoverClient.ts — the fetch half of the direct handover.
 *
 * `fulfillmentExport.ts` records why this was ever a file: their ERP was Flask
 * on a PC on the office LAN, an https page cannot POST to it (active mixed
 * content, a hard block), and it sent no CORS headers either. That stopped
 * applying when their database moved to Azure. This posts the same rows to
 * `/api/erp/handover`, which holds the connection string server-side and
 * writes only the five tables it is allowed to write.
 *
 * The body and the sendable-sheet rules are in erpHandoverBody.ts, which has
 * no auth import and so can be tested without standing up MSAL.
 *
 * ── IT DEFAULTS TO A DRY RUN, AND SO DOES THE SERVER ──────────────────────
 * Two defaults rather than one, because this is the only write in the mirror
 * and the cost of getting it wrong is rows in somebody else's production
 * database.
 */

import { getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";
import { ErpConsentRequiredError } from "../fulfillment/erpClient";
import type { IErpSheet } from "./fulfillmentExport";
import {
  buildChunkBody, buildHandoverBody, mergeResults, planHandoverRequests,
  HANDOVER_CHECK_ROWS, HANDOVER_REQUEST_ROWS, type IHandoverResult,
} from "./erpHandoverBody";

export { ErpConsentRequiredError };
export {
  handoverKeyFor, sendableSheets, buildHandoverBody, planHandoverRequests,
  buildChunkBody, mergeResults, wipOnly, gapSheets,
  HANDOVER_REQUEST_ROWS, HANDOVER_CHECK_ROWS,
  type IHandoverResult, type IHandoverTableResult, type IHandoverBody,
  type IHandoverChunk,
} from "./erpHandoverBody";

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return (body as { error?: string }).error || `Request failed (${res.status}).`;
  } catch {
    return `Request failed (${res.status}).`;
  }
}

async function postBody(body: unknown): Promise<IHandoverResult> {
  const token = await getApiTokenSilent();
  if (!token) throw new ErpConsentRequiredError();
  const res = await fetch("/api/erp/handover", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as IHandoverResult;
}

/** One request with every table in it. Kept for a small scope and for tests. */
export async function sendHandoverAtOnce(
  sheets: readonly IErpSheet[], dryRun: boolean
): Promise<IHandoverResult> {
  return postBody(buildHandoverBody(sheets, dryRun));
}

/**
 * The whole handover, as a SEQUENCE of requests — one table at a time, in
 * chunks. 224,894 rows cannot go in one body, so this is not an optimisation.
 *
 * SEQUENTIAL, not parallel, on purpose: each request takes one of the three
 * PostgreSQL connections the Function pool allows, and their own app is using
 * the same small server. A fan-out would finish sooner and could starve the
 * ERP its users are working in.
 *
 * STOPS AT THE FIRST FAILURE and throws with what had already been done, so a
 * half-finished write is reported as half-finished rather than as nothing.
 * Pressing Send again finishes it: every row already written is skipped.
 */
export class HandoverPartialError extends Error {
  constructor(message: string, readonly done: IHandoverResult | null) {
    super(message);
    this.name = "HandoverPartialError";
  }
}

export async function sendHandover(
  sheets: readonly IErpSheet[],
  dryRun: boolean,
  onProgress?: (done: number, total: number, label: string) => void
): Promise<IHandoverResult> {
  // A check sends keys only, so its requests can be five times the size.
  const chunks = planHandoverRequests(
    sheets, dryRun ? HANDOVER_CHECK_ROWS : HANDOVER_REQUEST_ROWS
  );
  let merged: IHandoverResult | null = null;
  for (let i = 0; i < chunks.length; i += 1) {
    const chunk = chunks[i];
    const label = chunk.of > 1
      ? `${chunk.sheet.name} (${chunk.index} of ${chunk.of})`
      : chunk.sheet.name;
    onProgress?.(i, chunks.length, label);
    try {
      merged = mergeResults(merged, await postBody(buildChunkBody(chunk, dryRun)));
    } catch (err) {
      throw new HandoverPartialError(
        `${label}: ${(err as Error)?.message ?? String(err)}`, merged
      );
    }
  }
  onProgress?.(chunks.length, chunks.length, "done");
  // An empty selection is not an error; it is a handover of nothing.
  return merged ?? {
    dryRun, wrote: false, at: new Date().toISOString(),
    totals: {
      received: 0, inserted: 0, filled: 0, alreadyThere: 0, noWip: 0,
      duplicateInPayload: 0, monitoringUpdated: 0,
    },
    tables: [],
  };
}
